import { Injectable } from "@angular/core";
import { AdministrationMenuViewModel } from "@modules/api/administration-menu-view-model.generated";
import { DashboardViewModel } from "@modules/api/dashboard-view-model.generated";
import { GetDynamicMenusService } from "@modules/api/get-dynamic-menus.service.generated";
import { PowerBIReportTypeEnum } from "@modules/api/power-bi-report-type-enum.generated";
import { PowerBIViewModel } from "@modules/api/power-bi-view-model.generated";
import { ConcurrentRequestHandlingEnum } from "@modules/common/enums/concurrent-request-handling-enum";
import { CurrentCaseService } from "@modules/common/services/current-case.service";
import { CurrentEnvironmentService } from '@modules/common/services/current-environment.service';
import { PermissionService } from "@modules/common/services/permission.service";
import { filter, Observable, ReplaySubject } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class GetCurrentUserDynamicMenusService {

    private readonly _dashboardsSubject = new ReplaySubject<DashboardViewModel[]>(1);
    private readonly _powerBIReportsSubject = new ReplaySubject<PowerBIViewModel[]>(1);
    private readonly _powerBIPaginatedReportsSubject = new ReplaySubject<PowerBIViewModel[]>(1);
    private readonly _administrationMenusSubject = new ReplaySubject<AdministrationMenuViewModel[]>(1);
    private _pending = false;

    constructor(
        private readonly _currentCaseService: CurrentCaseService,
        private readonly _currentEnvironmentService: CurrentEnvironmentService,
        private readonly _getDynamicMenusService: GetDynamicMenusService,
        private readonly _permissionService: PermissionService
    ) {
        this._currentCaseService.caseChanged.subscribe(() => this.updateMenus());
        this._currentEnvironmentService.environmentChanged.subscribe(() => this.updateMenus());
        this._permissionService.permissionsChanged.subscribe(() => this.updateMenus());
    }

    private updateMenus(): void {
        if (this._permissionService.verifyPermission('SSO into WEX')
            || this._currentCaseService.getCurrentCaseName() 
            && this._currentEnvironmentService.getCurrentEnvironmentName() 
            && (this._permissionService.verifyPermission('View Dashboards') 
            || this._permissionService.verifyPermission('View Power BI Reports'))) {

            this._pending = true;

            this._getDynamicMenusService.getDynamicMenus({
                concurrentRequestHandling: ConcurrentRequestHandlingEnum.LastOnly
            }).subscribe(result => {
                this._pending = false;
                this._dashboardsSubject.next(result && result.dashboards || []);
                this._powerBIReportsSubject.next(result && result.powerBIReports?.filter(x => x.powerBIReportType == PowerBIReportTypeEnum.Report) || []);
                this._powerBIPaginatedReportsSubject.next(result && result.powerBIReports?.filter(x => x.powerBIReportType == PowerBIReportTypeEnum.PaginatedReport) || []);
                this._administrationMenusSubject.next(result && result.administrationMenus || []);

            });
        } else {
            this._dashboardsSubject.next([]);
            this._powerBIReportsSubject.next([]);
            this._powerBIPaginatedReportsSubject.next([]);
            this._administrationMenusSubject.next([]);
        }
    }

    public getCurrentUserDashboards(): Observable<DashboardViewModel[]> {
        return this._dashboardsSubject.pipe(filter(x => !this._pending)); //Force new subscribers to wait for the new result if the service is pending, instead of getting the replay value
    }

    public getCurrentUserPowerBIReports(): Observable<PowerBIViewModel[]> {
        return this._powerBIReportsSubject.pipe(filter(x => !this._pending)); //Force new subscribers to wait for the new result if the service is pending, instead of getting the replay value
    }

    public getCurrentUserPowerBIPaginatedReports(): Observable<PowerBIViewModel[]> {
        return this._powerBIPaginatedReportsSubject.pipe(filter(x => !this._pending)); //Force new subscribers to wait for the new result if the service is pending, instead of getting the replay value
    }

    public getAdministrationMenus() : Observable<AdministrationMenuViewModel[]> {
        return this._administrationMenusSubject.pipe(filter(x => !this._pending)); //Force new subscribers to wait for the new result if the service is pending, instead of getting the replay value
    }
}